// Import from libraries
import { useMemo } from "react";
import { useRouteContext } from "@tanstack/react-router";
import { CSVLink } from "react-csv";
import { LuInfo, LuDownload, LuExternalLink } from "react-icons/lu";
import { BsFileEarmarkText } from "react-icons/bs";

// Import from scripts
import { useFileSubmissionErrors } from "@/queries";
import {
  FileStatus,
  type FileSubmission,
  type FileSubmissionUI,
} from "@/models/file-submission.tsx";
import { getUniqueCount } from "@/utils/data-structures";

// Import components
import Popup from "@/components/shared/Popup";
import Loader from "@/components/shared/Loader";
import Alert from "@/components/shared/Alert";

type FileStatusPopupProps = {
  show: boolean;
  onClose: () => void;
  file: FileSubmission | FileSubmissionUI;
};

type FileSubmissionError = NonNullable<
  ReturnType<typeof useFileSubmissionErrors>["data"]
>[number];

type ValidationErrorUI = FileSubmissionError & {
  IdentifierLabel: string;
  IdentifierValue: string;
};

/**
 * Returns the appropriate identifier label and value for a validation error
 * based on the record type. Child-related records use LocalChildId (CICN),
 * since CSPPID is optional on upload and often isn't populated; staff records
 * use LocalStaffId (with SEID fallback); classroom records use LocalClassroomId.
 */
function getIdentifierForError(err: FileSubmissionError): {
  label: string;
  value: string;
} {
  let parsed: Record<string, unknown> = {};
  try {
    if (err.Record) {
      parsed = JSON.parse(err.Record);
    }
  } catch {
    // ignore parse errors
  }

  const get = (key: string): string => {
    const v = parsed[key];
    return typeof v === "string" ? v.trim() : "";
  };

  switch (err.RecordType) {
    case "STFF":
    case "SASS":
    case "SCLR":
      return {
        label: "Staff ID",
        value: get("LocalStaffId") || get("SEID") || "—",
      };
    case "CLRM":
      return {
        label: "Classroom ID",
        value: get("LocalClassroomId") || "—",
      };
    case "CHLD":
    case "ENRL":
    case "CLEN":
    case "FAMI":
    case "LANG":
    case "SLNG":
    case "ATTN":
    default:
      return {
        label: "Local Child ID (CICN)",
        value: get("LocalChildId") || "—",
      };
  }
}

function statusPillClass(statusText: string): string {
  switch (statusText) {
    case "Posted":
      return "submission-status-pill submission-status-pill--posted";
    case "Failed":
      return "submission-status-pill submission-status-pill--failed";
    case "Rejected":
      return "submission-status-pill submission-status-pill--rejected";
    case "Pending":
      return "submission-status-pill submission-status-pill--pending";
    case "Processing":
      return "submission-status-pill submission-status-pill--processing";
    default:
      return "submission-status-pill submission-status-pill--default";
  }
}

function formatSubmittedDate(date: string | Date | undefined): string {
  if (!date) return "—";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function FileStatusPopup({
  show,
  onClose,
  file,
}: FileStatusPopupProps) {
  const currentUserOrg = useRouteContext({ from: "/$leaid" }).currentUserOrg;

  const statusText =
    (file as FileSubmissionUI).StatusName ??
    FileStatus.get(file.CurrentStatus.toString())?.Text ??
    "—";

  const isFailed = statusText === "Failed";

  const {
    data: errors,
    isLoading,
    error,
  } = useFileSubmissionErrors(currentUserOrg.esiIdPath, file.Id ?? "");

  // Extract the appropriate identifier (CSPPID / Staff ID / Classroom ID)
  const tableData: ValidationErrorUI[] = useMemo(
    () =>
      (errors ?? []).map((err) => {
        const { label, value } = getIdentifierForError(err);
        return { ...err, IdentifierLabel: label, IdentifierValue: value };
      }),
    [errors]
  );

  // Determine the dominant identifier label for the column header.
  // Single record type → "Staff ID" / "Local Child ID (CICN)"; mixed → "Identifier".
  const identifierColumnLabel = useMemo(() => {
    const labels = new Set(tableData.map((d) => d.IdentifierLabel));
    if (labels.size === 1)
      return tableData[0]?.IdentifierLabel ?? "Local Child ID (CICN)";
    return "Identifier";
  }, [tableData]);

  // Stats — driven by submission fields + the errors response
  const recordType =
    (file as { RecordTypeCode?: string }).RecordTypeCode ?? "—";
  const recordsInSubmission = file.InputRecords ?? 0;
  const totalErrors = file.Errors ?? 0;
  const recordsWithErrors = getUniqueCount(errors ?? [], "ErrorRecordId");
  const recordsWithNoErrors = Math.max(
    recordsInSubmission - recordsWithErrors,
    0
  );

  const csvHeaders = [
    { label: "Row #", key: "ErrorRecordId" },
    { label: identifierColumnLabel, key: "IdentifierValue" },
    { label: "Field Name", key: "Fieldname" },
    { label: "Error Code", key: "Code" },
    { label: "Error Message", key: "Message" },
  ];

  return (
    <Popup
      show={show}
      onClose={onClose}
      size="xl"
      className="submission-details-popup"
      header={
        <div className="d-flex align-items-center gap-3">
          <BsFileEarmarkText size={20} />
          <span style={{ fontWeight: 600, fontSize: "1.1rem" }}>
            Submission Details
          </span>
          <span className={statusPillClass(statusText)}>
            <LuInfo size={16} />
            {statusText}
          </span>
        </div>
      }
      hasButton={false}
      secondButtonText="Close"
      onSecondButtonClick={onClose}
    >
      {isFailed && (
        <Alert variant="danger" className="mb-3">
          <span className="fw-bold">Upload failed: </span>
          The CAPSDAC system was unable to read and process the file, so the
          upload failed. This was either due to a problem with the file itself
          or an issue with the CAPSDAC system. Check that the file is formatted
          correctly as a CSV and resubmit. If this problem persists, contact the
          CAPSDAC Support Team.
        </Alert>
      )}

      {isLoading ? (
        <Loader containerClass="py-3" />
      ) : error ? (
        <Alert variant="danger">
          <p className="mb-0">An error occurred retrieving the record data.</p>
        </Alert>
      ) : (
        <div className="submission-popup-body">
          {/* ---- Submission summary card (metadata + stats) ---- */}
          <div className="submission-card">
            <div className="d-flex align-items-center gap-3">
              <div className="submission-card__file-icon">
                <BsFileEarmarkText size={20} />
              </div>
              <div className="flex-grow-1">
                <div className="submission-meta-label">Submission File</div>
                <div className="submission-meta-filename">
                  {file.ClientFileName ?? "—"}
                </div>
              </div>
            </div>

            <hr className="submission-card__divider" />

            <div className="row g-3">
              <div className="col-md-3">
                <div className="submission-meta-label">LEA</div>
                <div className="submission-meta-value">
                  {currentUserOrg.orgName ?? "—"}
                </div>
              </div>
              <div className="col-md-3">
                <div className="submission-meta-label">Record Type</div>
                <div className="submission-meta-value">{recordType}</div>
              </div>
              <div className="col-md-3">
                <div className="submission-meta-label">Submitted</div>
                <div className="submission-meta-value">
                  {formatSubmittedDate(file.CreateDate)}
                </div>
              </div>
              <div className="col-md-3">
                <div className="submission-meta-label">Submitted By</div>
                <div className="submission-meta-value">
                  {file.CreatedByUserId ?? "—"}
                </div>
              </div>
            </div>

            <hr className="submission-card__divider" />

            <div className="row g-3">
              <div className="col-md-6">
                <StatTile
                  label="Total Errors"
                  value={totalErrors}
                  accent={totalErrors > 0 ? "danger" : "default"}
                />
              </div>
              <div className="col-md-6">
                <StatTile
                  label="Records in Submission"
                  value={recordsInSubmission}
                  accent="default"
                />
              </div>
              <div className="col-md-6">
                <StatTile
                  label="Records With No Errors"
                  value={recordsWithNoErrors}
                  accent="default"
                />
              </div>
              <div className="col-md-6">
                <StatTile
                  label="Records With Errors"
                  value={recordsWithErrors}
                  accent={recordsWithErrors > 0 ? "danger" : "default"}
                  labelIcon={
                    <LuInfo
                      size={16}
                      className="submission-stat-tile__info-icon"
                    />
                  }
                />
              </div>
            </div>
          </div>

          {!isFailed && (
            <div className="submission-card">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="validation-errors-section__title">
                  Validation Errors
                </div>
                <CSVLink
                  data={tableData}
                  headers={csvHeaders}
                  filename={`${file.ClientFileName ?? "submission"}-errors.csv`}
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                >
                  <LuDownload size={14} />
                  Export CSV
                </CSVLink>
              </div>

              <div className="validation-errors-table-wrapper">
                <table className="table mb-0 validation-errors-table">
                  <thead>
                    <tr>
                      <th className="validation-errors-table__col-row">
                        Row #
                      </th>
                      <th className="validation-errors-table__col-id">
                        {identifierColumnLabel}
                      </th>
                      <th className="validation-errors-table__col-field">
                        Field Name
                      </th>
                      <th className="validation-errors-table__col-code">
                        Error Code
                      </th>
                      <th>Error Message</th>
                      <th className="validation-errors-table__col-docs">
                        Docs
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="validation-errors-table__empty"
                        >
                          No validation errors found.
                        </td>
                      </tr>
                    ) : (
                      tableData.map((err, idx) => (
                        <tr key={`${err.Id}-${err.Code}-${idx}`}>
                          <td>{err.ErrorRecordId}</td>
                          <td>{err.IdentifierValue}</td>
                          <td>{err.Fieldname || "—"}</td>
                          <td>{err.Code}</td>
                          <td>{err.Message}</td>
                          <td>
                            <a
                              href="https://www.cde.ca.gov/sp/cd/ci/capsdacchanges.asp"
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`Documentation for ${err.Code}`}
                              className="validation-errors-table__docs-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <LuExternalLink size={16} />
                              <span className="visually-hidden">
                                External link to documentation for Error Code{" "}
                                {err.Code}
                              </span>
                            </a>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Popup>
  );
}

function StatTile({
  label,
  value,
  accent,
  labelIcon,
}: {
  label: string;
  value: number;
  accent: "default" | "danger";
  labelIcon?: React.ReactNode;
}) {
  const valueClass =
    accent === "danger"
      ? "submission-stat-tile__value submission-stat-tile__value--danger"
      : "submission-stat-tile__value";

  return (
    <div className="submission-stat-tile">
      <div className="submission-stat-tile__label">
        {label}
        {labelIcon}
      </div>
      <div className={valueClass}>{value}</div>
    </div>
  );
}
